import React from 'react';
import { Star, User } from 'lucide-react';

const StarDisplay = ({ rating, label }) => {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-sm text-gray-600 w-40">{label}:</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
      <span className="text-sm font-semibold text-gray-700">{rating}/5</span>
    </div>
  );
};

const ReviewsList = ({ reviews }) => {
  if (!reviews || reviews.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-gray-200">
        <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No reviews yet. Be the first to review this course!</p>
      </div>
    );
  }

  const calculateAverage = (reviews) => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => {
      return acc + review.teaching_methodology_rating + review.course_contents_rating + review.teacher_knowledge_rating;
    }, 0);
    return (sum / (reviews.length * 3)).toFixed(1);
  };

  const avgRating = calculateAverage(reviews);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-2xl p-6 border-2 border-yellow-200">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="text-5xl font-bold text-gray-900">{avgRating}</div>
            <div className="flex justify-center mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-5 h-5 ${
                    star <= Math.round(avgRating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-1">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex-1 text-gray-700">
            <p className="font-semibold mb-1">Overall Course Rating</p>
            <p className="text-sm">Based on student feedback across teaching, content, and knowledge</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {reviews.map((review) => (
          <div
            key={review.id}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200"
            data-testid={`review-${review.id}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-gray-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.student_name}</h4>
                    <p className="text-sm text-gray-500">
                      {new Date(review.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>

                <div className="mb-4 space-y-1">
                  <StarDisplay rating={review.teaching_methodology_rating} label="Teaching Methodology" />
                  <StarDisplay rating={review.course_contents_rating} label="Course Contents" />
                  <StarDisplay rating={review.teacher_knowledge_rating} label="Teacher's Knowledge" />
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-700 leading-relaxed">{review.review_text}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewsList;
